// LA GRABADORA DE NOTAS DE VOZ (B-986, 3-oct-2026) — la del chat del portal del paciente, con la misma lógica que la
// del chat del equipo (components/chat/ComposerChat.jsx) y la nota de voz de la cita: WebM si el navegador lo graba
// (Chrome, Android, Firefox), MP4 si no (el iPhone); el micrófono se suelta SIEMPRE al parar, al cancelar o al salir de
// la pantalla (si no, queda el punto rojo); y un corte a los 5 minutos por si alguien se deja el micrófono abierto.
// Aquí, además, la nota no se manda sola al parar: se puede escuchar y decidir (enviar o descartar), como en WhatsApp.
import { useEffect, useRef, useState } from 'react'

import { elegirMimeGrabacion, extensionDeNota, puedeGrabar } from '../utils/grabacion'
import { t as i18nT } from '../utils/i18n.js'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

export const MAX_SEG_NOTA = 300
export { elegirMimeGrabacion, extensionDeNota, puedeGrabar }

/**
 * @param {{ maxSeg?: number, onError?: (texto: string) => void }} o
 * @returns {{ estado: 'parada'|'pidiendo'|'grabando'|'lista', segundos: number, nota: { blob, url, mime, nombre, segundos } | null,
 *             empezar: () => Promise<void>, parar: () => void, cancelar: () => void, descartar: () => void }}
 */
export default function useGrabadoraNota({ maxSeg = MAX_SEG_NOTA, onError } = {}) {
  const [estado, setEstado] = useState('parada')
  const [segundos, setSegundos] = useState(0)
  const [nota, setNota] = useState(null)
  const mediaRef = useRef(null)
  const streamRef = useRef(null)
  const trozosRef = useRef([])
  const canceladoRef = useRef(false)
  const montadoRef = useRef(true)
  const segRef = useRef(0)
  const urlRef = useRef(null)   // la de la nota sin mandar, para soltarla al salir de la pantalla

  const soltarMicro = () => {
    try { streamRef.current && streamRef.current.getTracks().forEach((t) => t.stop()) } catch (_) { /* ya cerrado */ }
    streamRef.current = null
  }
  const quitarNota = () => setNota((n) => { if (n && n.url) { try { URL.revokeObjectURL(n.url) } catch (_) { /* nada */ } } return null })

  const empezar = async () => {
    if (estado === 'grabando' || estado === 'pidiendo') return
    quitarNota()
    setEstado('pidiendo')
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      if (!montadoRef.current) { stream.getTracks().forEach((t) => t.stop()); return }
      streamRef.current = stream
      const mime = elegirMimeGrabacion()
      const mr = new window.MediaRecorder(stream, mime ? { mimeType: mime } : undefined)
      trozosRef.current = []; canceladoRef.current = false; segRef.current = 0
      mr.ondataavailable = (ev) => { if (ev.data && ev.data.size) trozosRef.current.push(ev.data) }
      mr.onstop = () => {
        soltarMicro()
        const tipo = mr.mimeType || mime || 'audio/webm'
        const blob = new Blob(trozosRef.current, { type: tipo })
        trozosRef.current = []
        if (!montadoRef.current) return
        if (canceladoRef.current || !blob.size) { setEstado('parada'); return }
        const url = URL.createObjectURL(blob)
        urlRef.current = url
        setNota({ blob, url, mime: tipo, nombre: 'nota-de-voz.' + extensionDeNota(tipo), segundos: segRef.current })
        setEstado('lista')
      }
      mediaRef.current = mr
      mr.start()
      setSegundos(0)
      setEstado('grabando')
    } catch (e) {
      soltarMicro()
      if (!montadoRef.current) return
      setEstado('parada')
      const n = e && e.name
      onError && onError(n === 'NotAllowedError' || n === 'SecurityError'
        ? i18nT('hooks.useGrabadoraNota.no_hay_permiso_para_usar', 'No hay permiso para usar el micrófono. Puedes darlo en los ajustes del navegador o del móvil.')
        : n === 'NotFoundError' ? i18nT('hooks.useGrabadoraNota.no_se_encuentra_ningun_microfono', 'No se encuentra ningún micrófono en este dispositivo.')
        : i18nT('hooks.useGrabadoraNota.no_se_ha_podido_usar', 'No se ha podido usar el micrófono.'))
    }
  }
  const parar = () => {
    canceladoRef.current = false
    try { if (mediaRef.current && mediaRef.current.state !== 'inactive') mediaRef.current.stop() } catch (_) { /* nada que parar */ }
  }
  const cancelar = () => {
    canceladoRef.current = true
    try { if (mediaRef.current && mediaRef.current.state !== 'inactive') mediaRef.current.stop() } catch (_) { /* nada que parar */ }
    trozosRef.current = []
    soltarMicro()
    setEstado('parada')
  }
  const descartar = () => { quitarNota(); setEstado('parada') }

  // El contador y el corte de seguridad.
  useEffect(() => {
    if (estado !== 'grabando') return undefined
    const t = setInterval(() => {
      segRef.current += 1
      setSegundos(segRef.current)
      if (segRef.current >= maxSeg) parar()
    }, 1000)
    return () => clearInterval(t)
  }, [estado])
  // Al salir de la pantalla: el micrófono, fuera; y la nota que no se mandó, también.
  useEffect(() => {
    montadoRef.current = true
    return () => {
      montadoRef.current = false
      canceladoRef.current = true
      try { if (mediaRef.current && mediaRef.current.state !== 'inactive') mediaRef.current.stop() } catch (_) { /* ya parado */ }
      try { streamRef.current && streamRef.current.getTracks().forEach((t) => t.stop()) } catch (_) { /* ya cerrado */ }
      streamRef.current = null; mediaRef.current = null
      if (urlRef.current) { try { URL.revokeObjectURL(urlRef.current) } catch (_) { /* nada */ } urlRef.current = null }
    }
  }, [])

  return { estado, segundos, nota, empezar, parar, cancelar, descartar }
}
